/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Mantine
import {
	Box,
	LoadingOverlay,
	NumberFormatter,
	Text,
} from '@mantine/core';

// Styles
import classes from './Offsets.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';

// Types
import { useGetOffsets } from '@api/get-offsets';
import { Tag } from '@components/v2/ui/Tag';
import { TOffset } from '@global-types/api/api';
import { getCurrencyUnit } from '@utils/currency-units';
import dayjs from 'dayjs';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type RowExpansionProps = {
	offsetId: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Offsets = (props: RowExpansionProps) => {
	/* ******************************** Hooks ******************************* */
	const { t } = useTranslation();

	const { data, isLoading } = useGetOffsets({
		params: {
			entity: props?.offsetId,
			pageSize: 1000,
		},
		queryConfig: {
			enabled: !!props?.offsetId,
		},
	});

	const rowsOffset =
		Array.isArray(data) && data.length > 0 ? (
			data.map(
				(
					item: TOffset & {
						quantityParsed: string;
						priceTTC: number;
					}
				) => (
					<Box key={item.id} className={classes.row}>
						<Box className={classes.divition}>
							<Text className={classes.rowText}>
								{item.name}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.rowText}>
								{dayjs(item.createdAt).format(
									'DD/MM/YYYY'
								)}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.rowText}>
								{item.quantityParsed}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Tag
								type={item.status}
								label={item.status}
							/>
						</Box>
						<Box className={classes.divition}>
							<NumberFormatter
								value={item.priceTTC}
								className={classes.rowText}
								thousandSeparator=" "
								suffix={' ' + getCurrencyUnit('EUR')}
								decimalScale={2}
							/>
						</Box>
					</Box>
				)
			)
		) : (
			<Box className={classes.row}>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
				<Box className={classes.divition}>
					<Text className={classes.rowText}>
						{NOT_APPLICABLE}
					</Text>
				</Box>
			</Box>
		);

	/* ******************************** RENDERING ******************************* */
	return (
		<>
			{isLoading ? (
				<Box
					pos="relative"
					className={classes.containerOverlay}
				>
					<LoadingOverlay
						visible={true}
						zIndex={1000}
						overlayProps={{ radius: 'sm', blur: 2 }}
					/>
				</Box>
			) : (
				<Box className={classes.container}>
					<Box className={classes.header}>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.name')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.contributionDate')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.quantity')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('offsets.status')}
							</Text>
						</Box>
						<Box className={classes.divition}>
							<Text className={classes.headerText}>
								{t('global.totalPriceOffset')}
							</Text>
						</Box>
					</Box>

					<Box className={classes.row}>
						{/*Mesurability projects rows */}
						<Box className={classes.rowsDivision}>
							{rowsOffset}
						</Box>
					</Box>
				</Box>
			)}
		</>
	);
};
